import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import Filter from "../../../Assets/Images/CommanImages/filter.png";
import columnaddition from "../../../Assets/Images/CommanImages/flat-color-icons_add-column.png";
import updown from "../../../Assets/Images/CommanImages/updown.png";
import searchicon from "../../../Assets/Images/CommanImages/searchyellow.png";
import leftArrow from "../../../Assets/Images/CommanImages/Larrowbar.png";
import rightArrow from "../../../Assets/Images/CommanImages/Rarrowbar.png";
import FileExport from "../../../Assets/Images/CommanImages/fileexport.png";
import FileSaver from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import EndPoints from "../../../Api/Endpoints";

// API response column indices — matches the headers array exactly:
// 0: Ques Paper Id   (n*)
// 1: Ques Paper Name (t*)
// 2: Ques Paper Description (t*)
// 3: Status          (n*)
// 4: Course          (t*)
// 5: Course Plan     (t*)
// 6: Subject         (t*)
// 7: Chapter List    (t*)
// 8: Topic List      (t*)
// 9: Scheduled By    (t*)
// 10: Start Date     (d*)
// 11: End Date       (d*)
// 12: Course Id      (n*)

const COL = {
  QUES_PAPER_ID: 0,
  QUES_PAPER_NAME: 1,
  QUES_PAPER_DESC: 2,
  STATUS: 3,
  COURSE: 4,
  COURSE_PLAN: 5,
  SUBJECT: 6,
  CHAPTER_LIST: 7,
  TOPIC_LIST: 8,
  SCHEDULED_BY: 9,
  START_DATE: 10,
  END_DATE: 11,
  COURSE_ID: 12,
};

const TableComponent = ({
  headers,
  data,
  searchPlaceholder,
  dataClient,
  setPagePath,
}) => {
  const [isColumnDivVisible, setIsColumnDivVisible] = useState(false);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [averageRunTime, setAverageRunTime] = useState("");
  const [filterRunTime, setFilterRunTime] = useState("");
  const columnDropdownRef = useRef(null);
  const [selectedRows, setSelectedRows] = useState([]);

  // "Course Id" is a hidden utility column — never displayed in the table
  const HIDDEN_COLUMNS = ["Course Id", "Ques Paper Description", "Topic List"];

  useEffect(() => {
    if (headers.length > 0) {
      const displayableHeaders = headers.filter(
        (h) => !HIDDEN_COLUMNS.includes(h)
      );
      const initialColumns = displayableHeaders.slice(0, 6);
      if (
        !initialColumns.includes("Ques Paper Id") &&
        displayableHeaders.includes("Ques Paper Id")
      ) {
        initialColumns.unshift("Ques Paper Id");
      }
      setVisibleColumns(initialColumns);
    }
  }, [headers]);

  const handleColumnSelection = (column) => {
    if (column === "Ques Paper Id") return; // always visible, never toggle

    if (visibleColumns.includes(column)) {
      setVisibleColumns(visibleColumns.filter((col) => col !== column));
    } else {
      setVisibleColumns([...visibleColumns, column]);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterRunTimeChange = (e) => {
    setFilterRunTime(e.target.value);
  };

  const handleApplyFilter = () => {
    setAverageRunTime(filterRunTime);
    setIsFilterVisible(false);
    setCurrentPage(1);
  };

  const filteredData = (data || []).filter((row) => {
    const matchesSearchQuery = row.some(
      (cell) =>
        cell && String(cell).toLowerCase().includes(searchQuery.toLowerCase())
    );
    const matchesAverageRunTime =
      averageRunTime === "" || row[2] === averageRunTime;
    return matchesSearchQuery && matchesAverageRunTime;
  });

  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.key === column && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: column, direction });
  };

  const sortedData = React.useMemo(() => {
    let sortableData = [...filteredData];
    if (sortConfig.key !== null) {
      const colIndex = headers.indexOf(sortConfig.key);
      sortableData.sort((a, b) => {
        if (a[colIndex] < b[colIndex]) return sortConfig.direction === "asc" ? -1 : 1;
        if (a[colIndex] > b[colIndex]) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortableData;
  }, [filteredData, sortConfig, headers]);

  const handleExport = (type) => {
    const exportData = sortedData.map((row) =>
      visibleColumns.map((column) => row[headers.indexOf(column)])
    );
    const fileName = prompt("Enter the file name:");
    if (!fileName) return;

    if (type === "excel" || type === "csv") {
      const worksheet = XLSX.utils.aoa_to_sheet([visibleColumns, ...exportData]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      const fileExtension = type === "csv" ? "csv" : "xlsx";
      const mimeType =
        type === "csv"
          ? "text/csv"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const file = XLSX.write(workbook, { bookType: fileExtension, type: "array" });
      const blob = new Blob([file], { type: mimeType });
      FileSaver.saveAs(blob, `${fileName}.${fileExtension}`);
    } else if (type === "pdf") {
      const doc = new jsPDF();
      doc.autoTable({ head: [visibleColumns], body: exportData });
      doc.save(`${fileName}.pdf`);
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            style={{ borderRadius: "5px" }}
            className={i === currentPage ? "active" : ""}
          >
            {i}
          </button>
        );
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 3; i++) {
          pages.push(
            <button
              key={i}
              style={{ borderRadius: "5px" }}
              onClick={() => handlePageChange(i)}
              className={i === currentPage ? "active" : ""}
            >
              {i}
            </button>
          );
        }
        pages.push(<span style={{ borderRadius: "5px" }} key="dots1">...</span>);
        pages.push(
          <button key={totalPages} onClick={() => handlePageChange(totalPages)} style={{ borderRadius: "5px" }}>
            {totalPages}
          </button>
        );
      } else if (currentPage > 3 && currentPage < totalPages - 2) {
        pages.push(
          <button key={1} onClick={() => handlePageChange(1)} style={{ borderRadius: "5px" }}>1</button>
        );
        pages.push(<span style={{ borderRadius: "5px" }} key="dots1">...</span>);
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(
            <button
              key={i}
              onClick={() => handlePageChange(i)}
              style={{ borderRadius: "5px" }}
              className={i === currentPage ? "active" : ""}
            >
              {i}
            </button>
          );
        }
        pages.push(<span style={{ borderRadius: "5px" }} key="dots2">...</span>);
        pages.push(
          <button key={totalPages} onClick={() => handlePageChange(totalPages)} style={{ borderRadius: "5px" }}>
            {totalPages}
          </button>
        );
      } else {
        pages.push(
          <button key={1} onClick={() => handlePageChange(1)} style={{ borderRadius: "5px" }}>1</button>
        );
        pages.push(<span style={{ borderRadius: "5px" }} key="dots1">...</span>);
        for (let i = totalPages - 2; i <= totalPages; i++) {
          pages.push(
            <button
              key={i}
              onClick={() => handlePageChange(i)}
              style={{ borderRadius: "5px" }}
              className={i === currentPage ? "active" : ""}
            >
              {i}
            </button>
          );
        }
      }
    }
    return pages;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        columnDropdownRef.current &&
        !columnDropdownRef.current.contains(event.target)
      ) {
        setIsColumnDivVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "1.5%",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "82vw",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #6A6A6A",
              overflow: "hidden",
            }}
          >
            <input
              type="text"
              placeholder={searchPlaceholder}
              name="search"
              className="search-input-ques-dash"
              onChange={handleSearch}
            />
            <div
              style={{
                width: "1px",
                height: "24px",
                backgroundColor: "#2F2E40",
                margin: "0 4px",
              }}
            ></div>
            <img src={searchicon} alt="search" className="search-icon-ques-dash" />
          </div>
          <div style={{ textAlign: "end", marginTop: "20px", color: "#424242" }}>
            Displaying{" "}
            {Math.min((currentPage - 1) * pageSize + 1, sortedData.length)} -{" "}
            {Math.min(currentPage * pageSize, sortedData.length)} of{" "}
            {sortedData.length} records |
            {["10", "25", "50", "100", "500"].map((size) => (
              <span
                key={size}
                onClick={() => handlePageSizeChange(Number(size))}
                style={{
                  cursor: "pointer",
                  margin: "0 5px",
                  textDecoration: size === pageSize.toString() ? "none" : "underline",
                  color: size === pageSize.toString() ? "black" : "#337AB7",
                }}
              >
                {size}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          backgroundColor: "#D6D6D6",
          marginTop: "9px",
          borderTop: "1px solid grey",
          borderBottom: "1px solid grey",
        }}
      >
        <div
          style={{
            display: "flex",
            backgroundColor: "#FFBD20",
            alignItems: "center",
            cursor: "pointer",
            paddingLeft: "8px",
            width: "90px",
          }}
        >
          <img src={Filter} style={{ height: "24px", width: "24px" }} alt="filter" />
          <span style={{ marginLeft: "10px", color: "#2F2E40" }}>Filter</span>
        </div>

        <div
          style={{
            marginLeft: "auto",
            textAlign: "end",
            marginRight: "8px",
            display: "flex",
            alignItems: "center",
            position: "relative",
            cursor: "pointer",
            height: "40px",
          }}
          onClick={(e) => {
            if (!columnDropdownRef.current?.contains(e.target)) {
              setIsColumnDivVisible(!isColumnDivVisible);
            }
          }}
        >
          <img src={columnaddition} style={{ height: "34px", width: "32px" }} alt="column addition" />
          <span style={{ marginLeft: "4px", color: "#2F2E40", marginRight: "14px", fontSize: "14px" }}>
            Column Selection
          </span>
          {isColumnDivVisible && (
            <>
              <div className="arrow-up-tc"></div>
              <div className="popupColumn-tc" ref={columnDropdownRef}>
                {headers
                  .filter((column) => !HIDDEN_COLUMNS.includes(column))
                  .map((column, index) => {
                    const isLocked = column === "Ques Paper Id";
                    return (
                      <div
                        key={index}
                        onClick={() => !isLocked && handleColumnSelection(column)}
                        style={{ cursor: isLocked ? "default" : "pointer" }}
                      >
                        <input
                          type="checkbox"
                          id={`column-${index}`}
                          name="columnSelection"
                          value={column}
                          className="square-checkbox-tc"
                          checked={visibleColumns.includes(column)}
                          readOnly
                          disabled={isLocked}
                        />
                        <label
                          htmlFor={`column-${index}`}
                          style={{ color: isLocked ? "#999" : "#000" }}
                        >
                          {column}
                        </label>
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="table-wrapper-cols-selections">
        <table className="table-tc" style={{ width: "100%" }}>
          <thead>
            <tr>
              {visibleColumns.map((heading, index) => (
                <td
                  className="Heading-tc"
                  key={index}
                  style={{ textAlign: "left", cursor: "pointer" }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "left",
                      justifyContent: "left",
                      color: "#424242",
                    }}
                  >
                    <div>{heading}</div>
                    <img
                      onClick={() => handleSort(heading)}
                      src={updown}
                      style={{ height: "20px", width: "20px", marginLeft: "5px" }}
                      alt="sort"
                    />
                  </span>
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rowIndex) => (
              <tr key={rowIndex} className={rowIndex % 2 === 1 ? "even-row-tc" : ""}>
                {visibleColumns.map((column, cellIndex) => (
                  <td key={cellIndex} style={{ textAlign: "left", color: "#6A6A6A" }}>
                    <div
                      className="truncate-cell"
                      title={row[headers.indexOf(column)] || ""}
                    >
                      {/* First visible column (Ques Paper Id) is the edit link */}
                      {headers.indexOf(column) === COL.QUES_PAPER_ID ? (
                        <Link
                          style={{ color: "#337ab7" }}
                          to="/dpp_scheduling_edit"
                          state={{
                            // ── identifiers ──────────────────────────────
                            ques_paper_id:   row[COL.QUES_PAPER_ID],   // e.g. 84
                            course_id:       row[COL.COURSE_ID],        // e.g. 1  ← FIXED

                            // ── question paper detail (read-only display) ─
                            ques_paper_name: row[COL.QUES_PAPER_NAME],  // "TESTING-DPP-12TH..."
                            ques_paper_desc: row[COL.QUES_PAPER_DESC],  // "TESTING-DPP-12TH..." ← NEW
                            status:          row[COL.STATUS],           // "Pending Approval"
                            course:          row[COL.COURSE],           // "NEET"
                            course_plan:     row[COL.COURSE_PLAN],      // "All Subjects, Free plan"
                            subject_name:    row[COL.SUBJECT],          // "Biology" or ""
                            chapter_list:    row[COL.CHAPTER_LIST],     // null or "Chapter A, Chapter B"
                            topic_list:      row[COL.TOPIC_LIST],       // null or "Topic X, Topic Y" ← NEW
                            scheduled_by:    row[COL.SCHEDULED_BY],     // "Bose, Debabrata"
                            start_date:      row[COL.START_DATE],       // "03-19-2026 09:57 AM"
                            end_date:        row[COL.END_DATE],         // "03-19-2026 09:57 AM"
                          }}
                        >
                          {row[COL.QUES_PAPER_ID]}
                        </Link>
                      ) : (
                        row[headers.indexOf(column)]
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination-tc">
        <button
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          className="extreme-left-tc-pagination"
        >
          <img src={leftArrow} alt="" />
        </button>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="lefttc-pagination"
        >
          {"<"}
        </button>
        {renderPagination()}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="righttc-pagination"
        >
          {">"}
        </button>
        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="extreme-right-tc-pagination"
        >
          <img src={rightArrow} alt="" />
        </button>
      </div>

      {isFilterVisible && (
        <div className="filter-sidebar-tc">
          <div className="filter-content-tc" style={{ position: "relative" }}>
            <button
              className="repman-cross-btn"
              style={{
                position: "absolute",
                left: "-29px",
                height: "40px",
                width: "30px",
                color: "white",
                border: "none",
                fontSize: "20px",
                fontWeight: "700",
                borderRadius: "5px 0px 0px 5px",
              }}
              onClick={() => setIsFilterVisible(false)}
            >
              <p>X</p>
            </button>
            <h3 className="AdvanceF-heading-tc">Filter</h3>
            <div style={{ display: "flex", justifyContent: "end" }}>
              <button
                style={{
                  width: "30%",
                  height: "32px",
                  borderRadius: "5px",
                  border: "2px solid #2F2E40",
                  fontSize: "16px",
                  fontWeight: "700",
                  backgroundColor: "white",
                  marginRight: "8px",
                  marginTop: "10px",
                }}
                onClick={handleApplyFilter}
              >
                APPLY
              </button>
            </div>
            <input
              type="text"
              placeholder="Enter Average Run Time"
              value={filterRunTime}
              onChange={handleFilterRunTimeChange}
            />
          </div>
        </div>
      )}

      {isModalVisible && (
        <div>
          <div className="arrow-up-tc-QB"></div>
          <div className="arrow-up-tc-QB-model">
            <div className="modal-content-tc">
              <h3>Select Export File Type</h3>
              <button onClick={() => { handleExport("excel"); setIsModalVisible(false); }}>Excel</button>
              <button onClick={() => { handleExport("csv"); setIsModalVisible(false); }}>CSV</button>
              <button onClick={() => { handleExport("pdf"); setIsModalVisible(false); }}>PDF</button>
              <button onClick={() => setIsModalVisible(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DPPDashboardSchedule = ({
  setMessageBox,
  setIsMessageBoxVisible,
  setActiveImg,
  dataClient,
  setPagePath,
}) => {
  useEffect(() => {
    if (dataClient) {
      const userName = dataClient?.client_schema_name?.[6]?.[0]?.user_name || "";
      const clientBranchId = dataClient?.client_schema_name?.[6]?.[0]?.client_branch_id || null;
      const clientId = dataClient?.client_schema_name?.[6]?.[0]?.client_id || null;
      sessionStorage.setItem("user_name", userName);
      sessionStorage.setItem("clientBranchId", clientBranchId);
      sessionStorage.setItem("clientId", clientId);
    }
  }, [dataClient]);

  const [reportDashboardData, setReportDashboardData] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const clientBranchId = sessionStorage.getItem("clientBranchId");
      const accessToken = localStorage.getItem("other");

      if (!clientBranchId || !accessToken) {
        console.warn("Missing clientBranchId or accessToken");
        return;
      }

      const response = await fetch(
        `${EndPoints.PRISM_GET_DPP_SCHEDULE_DASHBOARD}?client_branch_id=${clientBranchId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard data: ${response.status}`);
      }

      const data = await response.json();

      // Strip type prefixes: "n*Ques Paper Id" → "Ques Paper Id"
      const processedHeaders = data.headers.map((h) => h.split("*")[1]);

      setReportDashboardData({
        headers: processedHeaders,
        rows: data.rows,
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  return (
    <div
      style={{
        height: "auto",
        width: "100%",
        paddingLeft: "20px",
        marginBottom: "40px",
      }}
    >
      <div className="" style={{ position: "sticky", top: "0", paddingLeft: "15px" }}>
        <div className="create-role-container-ques-dashboards">
          <p style={{ color: "#2F2E40", fontSize: "30px" }}>DPP Schedule Dashboard</p>
          <div style={{ display: "flex", justifyItems: "center", alignItems: "center" }}>
            <button
              className="create-test-section-btn"
              onClick={() => navigate("/dpp_scheduling")}
              style={{ marginRight: "18px", boxShadow: "0px 4px 4px 0px #00000040" }}
            >
              <span className="plus-icon-create">+</span>SCHEDULE
            </button>
          </div>
        </div>
        <p style={{ marginTop: "1%", color: "#2F2E40" }}>
          To view, create, edit and delete DPP schedule.
        </p>
        <div>
          <TableComponent
            headers={reportDashboardData.headers || []}
            data={reportDashboardData.rows || []}
            searchPlaceholder="Filter Questions..."
            dataClient={dataClient}
            setPagePath={setPagePath}
          />
        </div>
      </div>
    </div>
  );
};

export default DPPDashboardSchedule;